import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Play, Pause, Save, BarChart3, Calendar, DollarSign, TrendingUp } from 'lucide-react';
import { RBACWrapper } from './RBACWrapper';

interface TimeEntry {
  id: string;
  taskId: string;
  taskTitle: string;
  startTime: string;
  endTime?: string;
  duration: number;
  description: string;
  userId: string;
  userName: string;
}

interface TimeTrackingViewProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const TimeTrackingView: React.FC<TimeTrackingViewProps> = ({ projectId, userRole }) => {
  const [activeView, setActiveView] = useState<'entry' | 'reporting'>('entry');
  const [activeTimer, setActiveTimer] = useState<string | null>(null);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([
    {
      id: 'te-001',
      taskId: 'task-001',
      taskTitle: 'Complete Tyler ERP Setup Documentation',
      startTime: '2024-08-24T09:00:00Z',
      endTime: '2024-08-24T11:30:00Z',
      duration: 150,
      description: 'Reviewed network requirements and security protocols',
      userId: 'user-001',
      userName: 'Client Admin'
    },
    {
      id: 'te-002',
      taskId: 'task-002',
      taskTitle: 'Data Migration Planning',
      startTime: '2024-08-24T14:00:00Z',
      endTime: '2024-08-24T16:45:00Z',
      duration: 165,
      description: 'Analyzed legacy system data structures',
      userId: 'user-002',
      userName: 'Tyler Data Engineer'
    }
  ]);
  const [newTimeEntry, setNewTimeEntry] = useState({
    taskId: '',
    description: '',
    duration: 0
  });

  const startTimer = (taskId: string) => {
    setActiveTimer(taskId);
    console.log(`Starting timer for task: ${taskId}`);
  };

  const stopTimer = (taskId: string) => {
    setActiveTimer(null);
    console.log(`Stopping timer for task: ${taskId}`);
  };

  const logTime = async (timeData: any) => {
    try {
      const response = await fetch('/api/onboarding/log-time', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          ...timeData
        }),
      });

      if (response.ok) {
        const savedEntry = await response.json();
        setTimeEntries([...timeEntries, savedEntry]);
        setNewTimeEntry({ taskId: '', description: '', duration: 0 });
      }
    } catch (error) {
      console.error('Error logging time:', error);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getTotalTimeForTask = (taskId: string) => {
    return timeEntries
      .filter(entry => entry.taskId === taskId)
      .reduce((total, entry) => total + entry.duration, 0);
  };

  const views = [
    { id: 'entry', label: 'Time Entry', icon: Clock },
    { id: 'reporting', label: 'Reporting', icon: BarChart3 }
  ];

  const mockReportingData = {
    estimatedHours: 120,
    nonBillableHours: 25,
    billableHours: 95,
    totalHours: 120,
    totalCost: 12000,
    totalBilled: 14250,
    profit: 2250,
    projectBreakdown: [
      { task: 'Tyler ERP Setup Documentation', estimated: 40, actual: 45, billable: 40, cost: 4000, billed: 4800 },
      { task: 'Data Migration Planning', estimated: 50, actual: 48, billable: 45, cost: 4800, billed: 5400 },
      { task: 'Tyler SSO Integration', estimated: 30, actual: 27, billable: 25, cost: 2700, billed: 3000 }
    ]
  };

  return (
    <div className="h-full bg-white">
      <RBACWrapper userRole={userRole} requiredPermission="time_tracking" requiredAction="read">
        <div className="p-8">
          <div className="mb-8">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
              Time Tracking
            </h2>
            <p className="text-gray-600 text-lg">
              Track time spent on tasks and monitor project effort
            </p>
          </div>

          <div className="flex items-center space-x-1 bg-white border border-gray-200 rounded-lg p-1 mb-8 w-fit">
            {views.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveView(id as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  activeView === id 
                    ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeView}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              {activeView === 'entry' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2">
              <div className="p-6 mb-6 rounded-xl bg-white border border-gray-200 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <Clock className="w-6 h-6 text-[#4267A1] mr-3" />
                  Active Tasks
                </h3>
                
                <div className="space-y-4">
                  {['task-001', 'task-002', 'task-003'].map((taskId) => (
                    <div key={taskId} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {taskId === 'task-001' ? 'Complete Tyler ERP Setup Documentation' :
                             taskId === 'task-002' ? 'Data Migration Planning' :
                             'Configure Tyler SSO Integration'}
                          </h4>
                          <p className="text-sm text-gray-600">
                            Total: {formatDuration(getTotalTimeForTask(taskId))}
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {activeTimer === taskId ? (
                            <button
                              onClick={() => stopTimer(taskId)}
                              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200"
                            >
                              <Pause className="w-4 h-4" />
                              <span>Stop</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => startTimer(taskId)}
                              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-lg hover:shadow-lg hover:shadow-[#8A9150]/25 transition-all duration-200"
                            >
                              <Play className="w-4 h-4" />
                              <span>Start</span>
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {activeTimer === taskId && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center space-x-2 text-blue-800">
                            <Clock className="w-4 h-4" />
                            <span className="font-medium">Timer running...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Time Entries</h3>
                
                <div className="space-y-4">
                  {timeEntries.map((entry) => (
                    <div key={entry.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">{entry.taskTitle}</h4>
                          <p className="text-sm text-gray-600">{entry.description}</p>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-[#4267A1]">
                            {formatDuration(entry.duration)}
                          </span>
                          <p className="text-xs text-gray-500">{entry.userName}</p>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(entry.startTime).toLocaleDateString()} • 
                        {new Date(entry.startTime).toLocaleTimeString()} - 
                        {entry.endTime && new Date(entry.endTime).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Time Entry</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Task
                    </label>
                    <select
                      value={newTimeEntry.taskId}
                      onChange={(e) => setNewTimeEntry({...newTimeEntry, taskId: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4267A1] focus:border-transparent"
                    >
                      <option value="">Select a task</option>
                      <option value="task-001">Tyler ERP Setup Documentation</option>
                      <option value="task-002">Data Migration Planning</option>
                      <option value="task-003">Tyler SSO Integration</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={newTimeEntry.duration}
                      onChange={(e) => setNewTimeEntry({...newTimeEntry, duration: Number(e.target.value)})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4267A1] focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={newTimeEntry.description}
                      onChange={(e) => setNewTimeEntry({...newTimeEntry, description: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4267A1] focus:border-transparent resize-none"
                      rows={3}
                      placeholder="What did you work on?"
                    />
                  </div>
                  
                  <button
                    onClick={() => logTime(newTimeEntry)}
                    disabled={!newTimeEntry.taskId || !newTimeEntry.duration}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    <span>Log Time</span>
                  </button>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 text-[#4267A1] mr-2" />
                  Time Summary
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Today:</span>
                    <span className="font-semibold text-[#4267A1]">
                      {formatDuration(timeEntries.reduce((sum, entry) => sum + entry.duration, 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">This Week:</span>
                    <span className="font-semibold text-[#4267A1]">
                      {formatDuration(timeEntries.reduce((sum, entry) => sum + entry.duration, 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Project Total:</span>
                    <span className="font-semibold text-[#8A9150]">
                      {formatDuration(timeEntries.reduce((sum, entry) => sum + entry.duration, 0) * 3)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
              )}
              
              {activeView === 'reporting' && (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="p-6 rounded-xl bg-[#4267A1] border border-[#4267A1]/30 shadow-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-xl bg-white/10">
                          <Clock className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold text-white">{mockReportingData.estimatedHours}h</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">Estimated Hours</h3>
                      <p className="text-white/70 text-sm">Total project estimate</p>
                    </div>

                    <div className="p-6 rounded-xl bg-[#4267A1] border border-[#4267A1]/30 shadow-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-xl bg-white/10">
                          <BarChart3 className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold text-white">{mockReportingData.nonBillableHours}h</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">Non-Billable Hours</h3>
                      <p className="text-white/70 text-sm">Internal time spent</p>
                    </div>

                    <div className="p-6 rounded-xl bg-[#4267A1] border border-[#4267A1]/30 shadow-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-xl bg-white/10">
                          <DollarSign className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold text-white">{mockReportingData.billableHours}h</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">Billable Hours</h3>
                      <p className="text-white/70 text-sm">Client billable time</p>
                    </div>

                    <div className="p-6 rounded-xl bg-[#4267A1] border border-[#4267A1]/30 shadow-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-xl bg-white/10">
                          <TrendingUp className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold text-white">${mockReportingData.profit.toLocaleString()}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">Profit</h3>
                      <p className="text-white/70 text-sm">Project margin</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="p-6 rounded-xl bg-[#4267A1] border border-[#4267A1]/30 shadow-xl">
                      <h3 className="text-xl font-semibold text-white mb-6">Project Summary</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Total Hours:</span>
                          <span className="font-semibold text-white">{mockReportingData.totalHours}h</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Total Cost:</span>
                          <span className="font-semibold text-white">${mockReportingData.totalCost.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Total Billed:</span>
                          <span className="font-semibold text-white">${mockReportingData.totalBilled.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-white/20">
                          <span className="text-white font-medium">Profit Margin:</span>
                          <span className="font-bold text-[#8A9150] text-lg">
                            {((mockReportingData.profit / mockReportingData.totalBilled) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 rounded-xl bg-[#4267A1] border border-[#4267A1]/30 shadow-xl">
                      <h3 className="text-xl font-semibold text-white mb-6">Task Breakdown</h3>
                      <div className="space-y-4">
                        {mockReportingData.projectBreakdown.map((task, index) => (
                          <div key={index} className="p-4 bg-white/5 rounded-lg border border-white/10">
                            <h4 className="font-medium text-white mb-3">{task.task}</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-white/70">Estimated:</span>
                                <span className="ml-2 text-white">{task.estimated}h</span>
                              </div>
                              <div>
                                <span className="text-white/70">Actual:</span>
                                <span className="ml-2 text-white">{task.actual}h</span>
                              </div>
                              <div>
                                <span className="text-white/70">Billable:</span>
                                <span className="ml-2 text-white">{task.billable}h</span>
                              </div>
                              <div>
                                <span className="text-white/70">Billed:</span>
                                <span className="ml-2 text-white">${task.billed.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </RBACWrapper>
    </div>
  );
};
